import React, { useContext } from 'react'
import cn from 'classnames'
import { FixedSpacer, Typography } from '@grammarly/badger-ui'
import { SocialShareWidget } from '@components/share-widget/social-share-widget'
import { CtaButtonDescription } from '@components/item/cta-button'
import { ContentProps, ContentRenderer } from '@components/renderer'
import { renderRichTextMultiLineContent } from '@components/renderer/rich-text'
import { SectionContext } from '@contexts/section-context'
import { useBrowserService } from '@lib/hooks'
import { useGetAuthAndDeviceTypeCta } from '@lib/hooks/auth-and-device-type-cta'
import { TypeSectionHero, SectionHeroLayoutDir, SectionHeroDynamicRenderAs } from '@lib/types'
import { TitleSize } from '@lib/types/typography'
import { isUnresolvedLink } from '@lib/utils/contentful-entry'
import { isTypeComponentEmptyStub } from '@lib/types/content-types'
import { AnimatedHeroTitle } from './animated-hero-title'
import { KeychainHeroTitle } from './keychain-hero-title'
import { GrammarlyAtWork25HeroTitle } from './grammarly-at-work-25-hero-title'

import styles from './hero.module.scss'

export const getDescriptionComponent = (titleSize?: TitleSize) => {
  switch (titleSize) {
    case TitleSize.HXL:
      return Typography.C1
    case TitleSize.Jumbo1:
      return Typography.TXL
    case TitleSize.Jumbo2:
      return Typography.TXL
    default:
      return Typography.C2
  }
}

export const HeroTitle: React.FC<ContentProps<TypeSectionHero>> = (props) => {
  const {
    structure: {
      fields: { dynamicRenderAs },
    },
  } = props

  if (dynamicRenderAs?.includes(SectionHeroDynamicRenderAs.Bts2025AnimatedHero)) {
    return <AnimatedHeroTitle structure={props.structure} />
  }

  if (dynamicRenderAs?.includes(SectionHeroDynamicRenderAs.Bts2025Keychain)) {
    return <KeychainHeroTitle structure={props.structure} />
  }

  if (dynamicRenderAs?.includes(SectionHeroDynamicRenderAs.GrammarlyAtWork25)) {
    return <GrammarlyAtWork25HeroTitle structure={props.structure} />
  }

  return <DefaultHeroTitle structure={props.structure} />
}

const DefaultHeroTitle: React.FC<ContentProps<TypeSectionHero>> = (props) => {
  const {
    structure: {
      fields: { layoutDirection, title, titleSize, seoTitle, description, ctAs, socialShareWidget },
    },
  } = props

  const { isH1Section } = useContext(SectionContext)
  const { isMobile } = useBrowserService()

  const { getAuthAndDeviceTypeCta } = useGetAuthAndDeviceTypeCta()

  const EyebrowTitleComponent = Typography.C6
  const titleStyle = titleSize || 'T1'
  const TitleComponent = Typography[titleStyle]
  const DescriptionComponent = getDescriptionComponent(titleSize)

  return (
    <figcaption>
      <header>
        {seoTitle ? (
          <>
            <EyebrowTitleComponent as={isH1Section ? 'h1' : 'h2'} className={styles.seoTitle}>
              {seoTitle}
            </EyebrowTitleComponent>
            <TitleComponent as="h2">{renderRichTextMultiLineContent(title)}</TitleComponent>
          </>
        ) : (
          <TitleComponent as={isH1Section ? 'h1' : 'h2'}>{renderRichTextMultiLineContent(title)}</TitleComponent>
        )}
        {isMobile ? <FixedSpacer multiplier={3} /> : <FixedSpacer multiplier={5} />}
        <DescriptionComponent as="div">{renderRichTextMultiLineContent(description)}</DescriptionComponent>
      </header>
      {ctAs?.length && (
        <>
          <FixedSpacer multiplier={5} />
          <div
            className={cn(styles.ctasContainer, {
              [styles.ctasCentered]:
                layoutDirection === SectionHeroLayoutDir.Vertical ||
                layoutDirection === SectionHeroLayoutDir.VerticalWide,
            })}
          >
            <ContentRenderer structure={ctAs} />
          </div>
          <div className={styles.ctasDescriptionsContainer}>
            {ctAs
              .map(getAuthAndDeviceTypeCta)
              .filter((cta) => !isUnresolvedLink(cta))
              .filter((cta) => !isTypeComponentEmptyStub(cta))
              .map((cta) => (
                <CtaButtonDescription key={`description-${cta.sys.contentType.sys.id}-${cta.sys.id}`} structure={cta} />
              ))}
          </div>
        </>
      )}
      {socialShareWidget && (
        <>
          <FixedSpacer multiplier={5} />
          <SocialShareWidget structure={socialShareWidget} />
        </>
      )}
    </figcaption>
  )
}
